import { useState, useEffect, useMemo } from 'react';
import { LabelDefinition } from '@/types/label-budget';
import {
  getLabelDefinitions,
  getApiKeyMetadata,
} from '@/utils/label-budget-storage';
import { employeeDB, EmployeeRecord } from '@/utils/employee-indexeddb';
import {
  HEADQUARTER_FILTER_PREFIX,
  DEPARTMENT_FILTER_PREFIX,
  UNASSIGNED_HEADQUARTER,
  parseDepartmentHierarchy
} from '@/utils/department-utils';
import { NO_USAGE_ALIAS } from '../constants';
import type { ApiKeyMetadata } from '../../../types/label-budget';

export interface UseMetadataReturn {
  labelDefinitions: LabelDefinition[];
  setLabelDefinitions: React.Dispatch<React.SetStateAction<LabelDefinition[]>>;
  apiKeyMetadata: Record<string, Partial<ApiKeyMetadata>>;
  setApiKeyMetadata: React.Dispatch<React.SetStateAction<Record<string, Partial<ApiKeyMetadata>>>>;
  employeeData: Map<string, EmployeeRecord>;
  setEmployeeData: React.Dispatch<React.SetStateAction<Map<string, EmployeeRecord>>>;
  headquarterOptions: string[];
  departmentOptions: Map<string, string[]>;
  roles: string[];
  headquarterSelectValue: string;
  departmentSelectValue: string;
  activeHeadquarter: string;
  isDepartmentSelectionDisabled: boolean;
  normalizedDepartmentFilter: string;
}

export function useMetadata(selectedDepartment: string, _selectedRole: string): UseMetadataReturn {
  const [labelDefinitions, setLabelDefinitions] = useState<LabelDefinition[]>([]);
  const [apiKeyMetadata, setApiKeyMetadata] = useState<Record<string, Partial<ApiKeyMetadata>>>({});
  const [employeeData, setEmployeeData] = useState<Map<string, EmployeeRecord>>(new Map());

  const normalizedDepartmentFilter = selectedDepartment === NO_USAGE_ALIAS ? 'no-usage' : selectedDepartment;

  const headquarterSelectValue = (() => {
    if (normalizedDepartmentFilter === 'all' ||
        normalizedDepartmentFilter === 'no-mapping' ||
        normalizedDepartmentFilter === 'no-usage') {
      return normalizedDepartmentFilter;
    }
    if (normalizedDepartmentFilter.startsWith(DEPARTMENT_FILTER_PREFIX)) {
      const [, hq] = normalizedDepartmentFilter.split('::');
      return `${HEADQUARTER_FILTER_PREFIX}${hq || ''}`;
    }
    if (normalizedDepartmentFilter.startsWith(HEADQUARTER_FILTER_PREFIX)) {
      return normalizedDepartmentFilter;
    }
    return 'all';
  })();

  const activeHeadquarter = headquarterSelectValue.startsWith(HEADQUARTER_FILTER_PREFIX)
    ? headquarterSelectValue.slice(HEADQUARTER_FILTER_PREFIX.length)
    : '';

  const departmentSelectValue = (() => {
    if (!activeHeadquarter) return 'all';
    if (normalizedDepartmentFilter.startsWith(DEPARTMENT_FILTER_PREFIX)) {
      return normalizedDepartmentFilter;
    }
    return `${HEADQUARTER_FILTER_PREFIX}${activeHeadquarter}`;
  })();

  const isDepartmentSelectionDisabled = headquarterSelectValue === 'all' ||
    headquarterSelectValue === 'no-mapping' ||
    headquarterSelectValue === 'no-usage';

  // Load label definitions and metadata
  useEffect(() => {
    const loadLabelData = () => {
      const definitions = getLabelDefinitions();
      setLabelDefinitions(definitions);

      const metadata = getApiKeyMetadata();
      const metadataMap: Record<string, Partial<ApiKeyMetadata>> = {};
      metadata.forEach(m => {
        metadataMap[m.apiKeyId] = m;
      });
      setApiKeyMetadata(metadataMap);
    };

    loadLabelData();

    const loadEmployeeData = async () => {
      try {
        await employeeDB.init();
        const employees = await employeeDB.getAllEmployees();
        const employeeMap = new Map<string, EmployeeRecord>();

        employees.forEach(emp => {
          if (emp.apiKeyId) {
            employeeMap.set(emp.apiKeyId, emp);
          }
          employeeMap.set(emp.email.toLowerCase(), emp);
          if (emp.email.includes('@')) {
            const emailPrefix = emp.email.split('@')[0].toLowerCase();
            if (!employeeMap.has(emailPrefix)) {
              employeeMap.set(emailPrefix, emp);
            }
          }
        });
        setEmployeeData(employeeMap);
      } catch (error) {
        console.error('Failed to load employee data:', error);
      }
    };

    loadEmployeeData();

    const handleLabelSettingsChanged = () => {
      loadLabelData();
    };

    const handleEmployeeDataChanged = () => {
      loadEmployeeData();
    };

    window.addEventListener('labelSettingsChanged', handleLabelSettingsChanged);
    window.addEventListener('employeeDataChanged', handleEmployeeDataChanged);
    return () => {
      window.removeEventListener('labelSettingsChanged', handleLabelSettingsChanged);
      window.removeEventListener('employeeDataChanged', handleEmployeeDataChanged);
    };
  }, []);

  const { headquarterOptions, departmentOptions, roles } = useMemo(() => {
    const headquarterMap = new Map<string, Set<string>>();
    const roleSet = new Set<string>();
    const uniqueEmployees = new Set<EmployeeRecord>();

    employeeData.forEach(emp => uniqueEmployees.add(emp));

    Array.from(uniqueEmployees).forEach(employee => {
      if (!employee) return;

      let headquarter = employee.headquarter;
      let departmentUnit = employee.departmentUnit;

      if (!headquarter || (!departmentUnit && !employee.normalizedDepartment)) {
        const parsed = parseDepartmentHierarchy(employee.department);
        headquarter = headquarter || parsed.headquarter;
        departmentUnit = departmentUnit || parsed.department;
      }

      if (!headquarter && departmentUnit) {
        headquarter = UNASSIGNED_HEADQUARTER;
      }

      if (headquarter) {
        if (!headquarterMap.has(headquarter)) {
          headquarterMap.set(headquarter, new Set<string>());
        }
        if (departmentUnit) {
          headquarterMap.get(headquarter)!.add(departmentUnit);
        }
      }

      if (employee.role) {
        roleSet.add(employee.role);
      }
    });

    const headquarterOptionsList = Array.from(headquarterMap.keys()).sort((a, b) => a.localeCompare(b, 'ja'));
    const departmentOptionsList = new Map<string, string[]>();
    headquarterOptionsList.forEach(hq => {
      const departments = Array.from(headquarterMap.get(hq) || [])
        .sort((a, b) => a.localeCompare(b, 'ja'));
      departmentOptionsList.set(hq, departments);
    });

    return {
      headquarterOptions: headquarterOptionsList,
      departmentOptions: departmentOptionsList,
      roles: Array.from(roleSet).sort((a, b) => a.localeCompare(b, 'ja'))
    };
  }, [employeeData]);

  return {
    labelDefinitions,
    setLabelDefinitions,
    apiKeyMetadata,
    setApiKeyMetadata,
    employeeData,
    setEmployeeData,
    headquarterOptions,
    departmentOptions,
    roles,
    headquarterSelectValue,
    departmentSelectValue,
    activeHeadquarter,
    isDepartmentSelectionDisabled,
    normalizedDepartmentFilter,
  };
}
